import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { formatDateShort } from 'utils/DateUtils';
import axiosPrivate from 'config/axiosPrivate';
import { setCurrentRedaction } from 'store/AdminClient/RedactionData';
import { fetchAdminClientTextOrderDisplay } from 'store/AdminClient/OrderDisplay/features';
import { useTextStatusChange, isTextRevisionNotAllowedError } from 'utils/hooks/useTextStatusChange';
import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import {
  DownloadTextWordType,
  DownloadTextsListWordType,
} from 'components/shared/DownloadFile/DownloadTextWordType';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import TablePagination from 'components/shared/Pagination/TablePagination';
import { ExcelExportButton } from 'components/shared/ExcelExport/ExcelExportButton';

export function ProjectTextsTable({ texts, onStatusChangeBanner, onNewContentClick, isDetailsOpen = true, orderId, generationMessage, isGenerationError = false, orderTextAmount, totalTextsCount, onStatusChange, readOnly = false, textDetailsPath = null }) {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [localTexts, setLocalTexts] = useState(
    Array.isArray(texts) ? texts : []
  );

  useEffect(() => {
    setLocalTexts(Array.isArray(texts) ? texts : []);
  }, [texts]);

  const rows = useMemo(() => localTexts, [localTexts]);

  const [selectedTexts, setSelectedTexts] = useState(new Set());
  const [selectedFilter, setSelectedFilter] = useState('Tous');
  const [openStatusFor, setOpenStatusFor] = useState(null);
  const [openActionsFor, setOpenActionsFor] = useState(null);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);
  const [sortKey, setSortKey] = useState('SCORE_DESC');
  const [sortColumn, setSortColumn] = useState('score');
  const [sortDirection, setSortDirection] = useState('desc');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    keywords: true,
    score: true,
    added: true,
    updated: true,
    status: true,
    uxScore: false,
    seoScore: false,
  });
  const tableContainerRef = useRef(null);
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
  const excelExportWrapperRef = useRef(null);
  const [hoveredRowKey, setHoveredRowKey] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [isRevisionErrorModalOpen, setIsRevisionErrorModalOpen] = useState(false);

  const { handleStatusChange: handleTextStatusChange } = useTextStatusChange(
    (textId, nextStatus) => {
      // Appeler le callback pour recharger les données du projet
      if (onStatusChange) {
        onStatusChange(textId, nextStatus);
      }
    },
    (error, previousItem) => {
      if (isTextRevisionNotAllowedError(error)) {
        setIsRevisionErrorModalOpen(true);
      }
      if (!previousItem) return;
      setLocalTexts((prev) =>
        Array.isArray(prev)
          ? prev.map((text) =>
            text.id === previousItem.id ? previousItem : text
          )
          : prev
      );
    },
    (textId, nextValue) => {
      const statusFlags = {
        Brouillon: {
          status: 'Brouillon',
          delivered: false,
          validated: false,
          deployed: false,
          inRevision: false,
        },
        Livré: {
          status: 'Livré',
          delivered: true,
          validated: false,
          deployed: false,
          inRevision: false,
        },
        'En révision': {
          status: 'En révision',
          delivered: false,
          validated: false,
          deployed: false,
          inRevision: true,
        },
        Validé: {
          status: 'Validé',
          delivered: true,
          validated: true,
          deployed: false,
          inRevision: false,
        },
        'En ligne': {
          status: 'En ligne',
          delivered: true,
          validated: true,
          deployed: true,
          inRevision: false,
        },
      };

      const flags = statusFlags[nextValue] || statusFlags.Brouillon;
      setLocalTexts((prev) =>
        Array.isArray(prev)
          ? prev.map((text) =>
            text.id === textId
              ? {
                ...text,
                status: flags.status,
                delivered: flags.delivered,
                validated: flags.validated,
                deployed: flags.deployed,
                inRevision: flags.inRevision,
              }
              : text
          )
          : prev
      );

      if (onStatusChangeBanner) {
        onStatusChangeBanner(flags.status);
      }
    }
  );

  const isWriterClientView =
    typeof window !== 'undefined' &&
    window.location.pathname.includes('/WriterClient/');

  const isTextInRevision = (text) => !!text?.inRevision;

  const getTextStatusLabel = (text) => {
    if (text?.deployed) return t('En ligne');
    if (text?.validated) return t('Validé');
    if (isTextInRevision(text)) return t('En révision');
    if (text?.delivered) return t('Livré');
    return t('Brouillon');
  };

  const getTextStatusIcon = (text) => {
    if (text?.deployed) return '/Images/deployed-text-icon.png';
    if (text?.validated) return '/Images/validate-text-icon.png';
    if (isTextInRevision(text)) return '/Images/en_revision.png';
    if (text?.delivered) return '/Images/delivred-text-icon.png';
    return '/Images/drafted-text-icon.png';
  };

  const getStatusKey = (text) => {
    if (text?.deployed) return 'En lignes';
    if (text?.validated) return 'Validés';
    if (isTextInRevision(text)) return 'En révision';
    if (text?.delivered) return 'Livrés';
    return 'Brouillon';
  };

  const getStatusIconByKey = (statusKey) => {
    switch (statusKey) {
      case 'En lignes':
        return '/Images/deployed-text-icon.png';
      case 'Validés':
        return '/Images/validate-text-icon.png';
      case 'Livrés':
        return '/Images/delivred-text-icon.png';
      case 'En révision':
        return '/Images/en_revision.png';
      case 'Brouillon':
      default:
        return '/Images/drafted-text-icon.png';
    }
  };

  const getTextScore = (text) => {
    const score = text?.contentScore?.globalScore ?? null;
    if (typeof score === 'number') return `${Math.round(score)}%`;
    return '-';
  };

  const formatDateSafe = (value) => {
    if (!value) return '-';
    try {
      return formatDateShort(value);
    } catch {
      return String(value);
    }
  };

  // Fonction pour gérer le clic sur les en-têtes de colonnes
  const handleColumnHeaderClick = (columnKey) => {
    if (columnKey === sortColumn) {
      // Toggle direction si on clique sur la même colonne
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Nouvelle colonne : définir la direction par défaut
      setSortColumn(columnKey);
      if (columnKey === 'keywords') {
        setSortDirection('asc'); // alphabétique A-Z par défaut
      } else if (columnKey === 'score' || columnKey === 'uxScore' || columnKey === 'seoScore') {
        setSortDirection('desc'); // score décroissant par défaut
      } else if (columnKey === 'added' || columnKey === 'updated') {
        setSortDirection('desc'); // plus récent par défaut
      } else if (columnKey === 'status') {
        setSortDirection('asc'); // Brouillon -> En ligne
      }
    }
  };

  const filterCounts = useMemo(() => {
    const counts = {
      Tous: rows.length,
      Brouillon: 0,
      Livrés: 0,
      'En révision': 0,
      Validés: 0,
      'En lignes': 0,
    };

    rows.forEach((text) => {
      if (text?.deployed) {
        counts['En lignes']++;
      } else if (text?.validated) {
        counts['Validés']++;
      } else if (isTextInRevision(text)) {
        counts['En révision']++;
      } else if (text?.delivered) {
        counts['Livrés']++;
      } else {
        counts['Brouillon']++;
      }
    });

    return counts;
  }, [rows]);


  const filteredRows = useMemo(() => {
    let filtered = rows;


    if (selectedFilter !== 'Tous') {
      filtered = filtered.filter((text) => {
        if (selectedFilter === 'En lignes') return text?.deployed;
        if (selectedFilter === 'Validés') return text?.validated && !text?.deployed;
        if (selectedFilter === 'Livrés') return text?.delivered && !text?.validated && !text?.deployed;
        if (selectedFilter === 'En révision') {
          return isTextInRevision(text) && !text?.validated && !text?.deployed;
        }
        if (selectedFilter === 'Brouillon') {
          return !text?.delivered && !text?.validated && !text?.deployed && !isTextInRevision(text);
        }
        return true;
      });
    }

    // Filtrage par recherche
    if (searchValue && searchValue.trim()) {
      const searchLower = searchValue.toLowerCase().trim();
      filtered = filtered.filter((text) => {
        const keyword = (text?.keyword || '').toLowerCase();
        const textTitle = (text?.textTitle || '').toLowerCase();
        const url = (text?.url || '').toLowerCase();
        const writerName = (text?.writer?.firstName || text?.writer?.lastName || text?.writer?.mail || text?.writer?.name || '').toLowerCase();
        const writerFullName = text?.writer
          ? `${text.writer.firstName || ''} ${text.writer.lastName || ''}`.toLowerCase().trim()
          : '';
        const creatorName = (text?.creator?.firstName || text?.creator?.lastName || text?.creator?.mail || text?.creator?.name || '').toLowerCase();
        const creatorFullName = text?.creator
          ? `${text.creator.firstName || ''} ${text.creator.lastName || ''}`.toLowerCase().trim()
          : '';

        return (
          keyword.includes(searchLower) ||
          textTitle.includes(searchLower) ||
          url.includes(searchLower) ||
          writerName.includes(searchLower) ||
          writerFullName.includes(searchLower) ||
          creatorName.includes(searchLower) ||
          creatorFullName.includes(searchLower)
        );
      });
    }

    return filtered;
  }, [rows, selectedFilter, searchValue]);


  useEffect(() => {
    const currentIds = new Set(filteredRows.map((text) => text.id ?? text.index));
    setSelectedTexts((prev) => {
      const filtered = new Set();
      prev.forEach((id) => {
        if (currentIds.has(id)) {
          filtered.add(id);
        }
      });
      return filtered;
    });
  }, [filteredRows]);

  const selectedRows = useMemo(
    () =>
      rows.filter((text) => {
        const key = text.id ?? text.index;
        return selectedTexts.has(key);
      }),
    [rows, selectedTexts]
  );

  const selectedExportIds = useMemo(
    () =>
      rows
        .filter((text) => selectedTexts.has(text.id ?? text.index))
        .map((text) => text.id)
        .filter(Boolean),
    [rows, selectedTexts]
  );

  const handleDownloadAllExcel = () => {
    if (!excelExportWrapperRef.current) return;

    try {
      const button =
        excelExportWrapperRef.current.querySelector(
          '.excel-export-wrapper-all .excel-export-button'
        );
      if (button) {
        button.click();
      }
    } catch (error) {
      console.error('Error triggering Excel export (all)', error);
    }

    setIsDownloadMenuOpen(false);
  };

  const handleDownloadSelectedExcel = () => {
    if (!excelExportWrapperRef.current) return;

    try {
      const button =
        excelExportWrapperRef.current.querySelector(
          '.excel-export-wrapper-selected .excel-export-button'
        );
      if (button) {
        button.click();
      }
    } catch (error) {
      console.error('Error triggering Excel export (selected)', error);
    }

    setIsDownloadMenuOpen(false);
  };

  const selectedOnPageCount = useMemo(
    () =>
      filteredRows.reduce((count, text, index) => {
        const key = text.id ?? text.index;
        const pageIndex = Math.floor(index / pageSize) + 1;
        if (pageIndex !== currentPage) return count;
        return selectedTexts.has(key) ? count + 1 : count;
      }, 0),
    [filteredRows, selectedTexts, currentPage]
  );

  const sortedRows = useMemo(() => {
    const base = [...filteredRows];

    const getGlobalScore = (text) =>
      typeof text?.contentScore?.globalScore === 'number'
        ? text.contentScore.globalScore
        : null;

    const getCreatedAt = (text) => {
      const raw = text.createdAt || text.dateCreation || text.updatedAt || text.modificationDate;
      const ts = raw ? new Date(raw).getTime() : 0;
      return Number.isNaN(ts) ? 0 : ts;
    };

    const getUpdatedAt = (text) => {
      const raw = text.updatedAt || text.lastUpdate || text.modificationDate || null;
      const ts = raw ? new Date(raw).getTime() : 0;
      return Number.isNaN(ts) ? 0 : ts;
    };

    const getKeyword = (text) => (text?.keyword || '').toLowerCase();

    const getStatusOrder = (text) => {
      if (text?.deployed) return 5; // En ligne
      if (text?.validated) return 4; // Validé
      if (isTextInRevision(text)) return 3; // En révision
      if (text?.delivered) return 2; // Livré
      return 1; // Brouillon
    };

    base.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'keywords': {
          const keywordA = getKeyword(a);
          const keywordB = getKeyword(b);
          const urlA = getWebsiteUrl(a);
          const urlB = getWebsiteUrl(b);

          // Tri par mot-clé d'abord, puis par URL si égal
          comparison = keywordA.localeCompare(keywordB);
          if (comparison === 0) {
            comparison = urlA.localeCompare(urlB);
          }
          break;
        }
        case 'score': {
          const sa = getGlobalScore(a) ?? -Infinity;
          const sb = getGlobalScore(b) ?? -Infinity;
          comparison = sa - sb;
          break;
        }
        case 'added': {
          const da = getCreatedAt(a);
          const db = getCreatedAt(b);
          comparison = da - db;
          break;
        }
        case 'updated': {
          const da = getUpdatedAt(a);
          const db = getUpdatedAt(b);
          comparison = da - db;
          break;
        }
        case 'status': {
          const sa = getStatusOrder(a);
          const sb = getStatusOrder(b);
          comparison = sa - sb;
          break;
        }
        case 'uxScore': {
          const uxA = typeof a?.contentScore?.uxScore === 'number' ? a.contentScore.uxScore : -Infinity;
          const uxB = typeof b?.contentScore?.uxScore === 'number' ? b.contentScore.uxScore : -Infinity;
          comparison = uxA - uxB;
          break;
        }
        case 'seoScore': {
          const seoA = typeof a?.contentScore?.seoScore === 'number' ? a.contentScore.seoScore : -Infinity;
          const seoB = typeof b?.contentScore?.seoScore === 'number' ? b.contentScore.seoScore : -Infinity;
          comparison = seoA - seoB;
          break;
        }
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return base;
  }, [filteredRows, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);

  const paginatedRows = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedRows.slice(startIndex, endIndex);
  }, [sortedRows, safeCurrentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilter, searchValue]);

  const columnDefs = useMemo(
    () => [
      {
        key: 'keywords',
        header: t('Mots clés & URL'),
        width: 'minmax(0, 3fr)',
        sortable: true,
      },
      {
        key: 'uxScore',
        header: t('Score UX'),
        width: '100px',
        align: 'center',
        sortable: true,
      },
      {
        key: 'seoScore',
        header: t('Score SEO'),
        width: '100px',
        align: 'center',
        sortable: true,
      },
      {
        key: 'score',
        header: t('Score'),
        width: '80px',
        align: 'center',
        sortable: true,
      },
      {
        key: 'added',
        header: t('Ajout'),
        width: '120px',
        align: 'center',
        sortable: true,
      },
      {
        key: 'updated',
        header: t('Mise à jour'),
        width: '120px',
        align: 'center',
        sortable: true,
      },
      {
        key: 'status',
        header: t('Status'),
        width: '120px',
        align: 'center',
        sortable: true,
      },
    ],
    [t]
  );

  const activeColumns = useMemo(
    () => columnDefs.filter((c) => visibleColumns[c.key]),
    [columnDefs, visibleColumns]
  );

  const handleActionsClick = (e, textId) => {
    e.stopPropagation();
    setOpenActionsFor((prev) => (prev === textId ? null : textId));
  };

  const getWebsiteUrl = (text) => {
    return text?.url || '-';
  };

  const handleEditClick = async (text, e) => {
    e.stopPropagation();

    if (readOnly) {
      if (textDetailsPath && text?.id) {
        try {
          window.localStorage.setItem('AdminClient_TextId', String(text.id));
        } catch (err) { }
        history.push(`${textDetailsPath}/${text.id}`);
      }
      return;
    }

    try {
      let d = null;
      try {
        const res = await axiosPrivate.get(`/texts/external/${text?.id}/fullDetails`);
        d = res?.data || null;
      } catch (e) { }

      dispatch(
        setCurrentRedaction({
          id: (d && d.id) || text?.id,
          title: (d && (d.textTitle || d.keyword)) || text?.keyword || '',
          textTitle: (d && (d.textTitle || d.keyword)) || text?.keyword || '',
          keyword: (d && d.keyword) || text?.keyword || '',
          url: (d && d.url) || '',
          titleTag: (d && d.titleTag) || '',
          metaDescriptionTag: (d && d.metaDescriptionTag) || '',
          initialHtmlContent: (d && d.initialHtmlContent) || '',
          initialTextContent: (d && d.initialTextContent) || '',
          actualHtmlContent: (d && d.actualHtmlContent) || '',
          actualTextContent: (d && d.actualTextContent) || '',
          semanticGap: (d && d.semanticGap) || [],
          analyseResult: (d && d.analyseResult) || null,
          stuffedKeywords: (d && d.stuffedKeywords) || [],
          internalLinking: (d && d.internalLinking) || '',
          delivered: !!(d && d.delivered),
          inRevision: !!(d && d.inRevision),
          revisionSent: !!(d && d.revisionSent),
          revisionDone: !!(d && d.revisionDone),
          globalRevisionSent: !!(d && d.globalRevisionSent),
          validated: !!(d && d.validated),
          orderId: (d && d.projectId) || null,
          createdAt: (d && d.createdAt) || text?.createdAt || new Date().toISOString(),
          initialScoreData: (d && d.initialScoreData) || null,
          contentScore: (d && d.contentScore) || null,
        })
      );

      const isWriterClient = window.location.pathname.includes('/WriterClient/');
      const isProjectManager = window.location.pathname.includes('/ProjectManager/');
      const isConsultant = window.location.pathname.includes('/Consultant/');

      if (textDetailsPath) {
        const textId = (d && d.id) || text?.id;
        try {
          if (textId) window.localStorage.setItem('AdminClient_TextId', String(textId));
        } catch (err) { }
        history.push(textId ? `${textDetailsPath}/${textId}` : textDetailsPath);
        return;
      }

      if (isWriterClient) {
        history.push('/WriterClient/TextDetails');
      } else if (isProjectManager) {
        history.push('/ProjectManager/TextDetails');
      } else if (isConsultant) {
        history.push('/Consultant/TextDetails');
      } else {
        const textId = (d && d.id) || text?.id;
        try {
          if (textId) window.localStorage.setItem('AdminClient_TextId', String(textId));
        } catch (err) { }
        history.push(textId ? `/AdminClient/TextDetails/${textId}` : '/AdminClient/TextDetails');
      }
    } catch (e) { }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const pageIds = new Set(
        paginatedRows.map((text) => text.id ?? text.index)
      );
      setSelectedTexts((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    } else {
      setSelectedTexts((prev) => {
        const next = new Set(prev);
        paginatedRows.forEach((text) => {
          const key = text.id ?? text.index;
          next.delete(key);
        });
        return next;
      });
    }
  };

  const handleSelectText = (textId, checked) => {
    setSelectedTexts((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(textId);
      } else {
        next.delete(textId);
      }
      return next;
    });
  };

  const handleStatusIconClick = (e, textId) => {
    if (readOnly) return;
    e.stopPropagation();
    setOpenStatusFor((prev) => (prev === textId ? null : textId));
  };

  const normalizeStatusKeyForHook = (statusKey) => {
    if (statusKey === 'Livrés') return 'Livré';
    if (statusKey === 'Validés') return 'Validé';
    if (statusKey === 'En lignes') return 'En ligne';
    if (statusKey === 'En révision') return 'En révision';
    return statusKey; // 'Brouillon'
  };

  const handleClearSelection = () => {
    setSelectedTexts(new Set());
  };

  const openDeleteConfirmation = (textsToDelete) => {
    const validTexts = (textsToDelete || []).filter((t) => t && t.id);
    if (!validTexts.length) return;

    const ids = validTexts.map((t) => t.id);
    setPendingDeleteIds(ids);

    if (ids.length === 1) {
      const text = validTexts[0];
      const name = text?.textTitle || text?.keyword || text?.id;
      setDeleteConfirmMessage(
        t('Êtes-vous sûr de vouloir supprimer le texte "{{name}}" ?', {
          name,
        })
      );
    } else {
      setDeleteConfirmMessage(
        t(
          'Êtes-vous sûr de vouloir supprimer {{count}} textes ? Cette action est irréversible.',
          { count: ids.length }
        )
      );
    }

    setIsDeleteConfirmOpen(true);
  };

  const executeDelete = async () => {
    const ids = pendingDeleteIds || [];
    if (!ids.length) {
      setIsDeleteConfirmOpen(false);
      return;
    }

    try {
      await Promise.all(
        ids.map((id) =>
          axiosPrivate.delete(`/texts/external/${id}/delete`).catch(() => null)
        )
      );

      setShowDeleteSuccess(true);
      setSelectedTexts(new Set());
      if (orderId) {
        setTimeout(() => {
          dispatch(fetchAdminClientTextOrderDisplay(orderId));
        }, 1500);
      }
    } finally {
      setIsDeleteConfirmOpen(false);
      setPendingDeleteIds([]);
    }
  };

  const isAllSelected =
    paginatedRows.length > 0 &&
    paginatedRows.every((text) =>
      selectedTexts.has(text.id ?? text.index)
    );
  const isIndeterminate =
    selectedTexts.size > 0 && !isAllSelected && paginatedRows.some((text) => selectedTexts.has(text.id ?? text.index));

  // Mettre à jour selectedTexts quand le filtre change
  useEffect(() => {
    setSelectedTexts(new Set());
  }, [selectedFilter]);


  useEffect(() => {
    if (!openStatusFor && !openActionsFor && !isSortOpen && !isColumnsOpen) return;

    const handleClickOutside = (event) => {
      if (tableContainerRef.current && !tableContainerRef.current.contains(event.target)) {
        setOpenStatusFor(null);
        setOpenActionsFor(null);
        setIsSortOpen(false);
        setIsColumnsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpenStatusFor(null);
        setOpenActionsFor(null);
        setIsSortOpen(false);
        setIsColumnsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openStatusFor, openActionsFor, isSortOpen, isColumnsOpen]);

  const filterOptions = [
    { key: 'Tous', label: t('Tous') },
    { key: 'Brouillon', label: t('Brouillon') },
    { key: 'Livrés', label: t('Livrés') },
    { key: 'En révision', label: t('En révision') },
    { key: 'Validés', label: t('Validés') },
    { key: 'En lignes', label: t('En lignes') },
  ];

  const hasSelection = selectedTexts.size > 0;

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
      <div
        ref={tableContainerRef}
        style={{ marginTop: 24, padding: '0 16px 64px', position: 'relative' }}
      >
        {!!orderId && (
          <div ref={excelExportWrapperRef} style={{ display: 'none' }}>
            <div className="excel-export-wrapper-all">
              <ExcelExportButton orderId={orderId} />
            </div>
            <div className="excel-export-wrapper-selected">
              <ExcelExportButton textIds={selectedExportIds} />
            </div>
          </div>
        )}

        <ProjectCreatedModal
          isOpen={showDeleteSuccess}
          onClose={() => setShowDeleteSuccess(false)}
          onConfirm={() => setShowDeleteSuccess(false)}
          isError={false}
          title={t('Texte supprimé')}
          message={t('Le texte a bien été supprimé.')}
          confirmButtonText={t('Fermer')}
        />

        <ProjectCreatedModal
          isOpen={isDeleteConfirmOpen}
          onClose={() => setIsDeleteConfirmOpen(false)}
          onConfirm={executeDelete}
          title={t('Supprimer le texte ?')}
          message={
            deleteConfirmMessage ||
            t(
              'Êtes-vous sûr de vouloir supprimer ce texte ? Cette action est irréversible.'
            )
          }
          confirmButtonText={t('Supprimer')}
        />

        <QuitProjectCreationModal
          isOpen={isRevisionErrorModalOpen}
          onClose={() => setIsRevisionErrorModalOpen(false)}
          variant="enrichment"
          title={t('Mise en révision impossible')}
          message={t(
            'Ce texte est validé ou mis en ligne. Il ne peut pas être envoyé en révision.'
          )}
          primaryButtonLabel={t('Fermer')}
          onPrimaryAction={() => setIsRevisionErrorModalOpen(false)}
          showSecondaryButton={false}
          showSuggestion={false}
        />

        {/* Filtres */}
        <div
          className="project-texts-table-toolbar"
          style={{
            display: 'flex',
            gap: '24px',
            marginBottom: '16px',
            marginLeft: '-18px',
            borderBottom: '1px solid #E5E7EB',
            paddingBottom: '4px',
            alignItems: 'center',
          }}
        >
          {filterOptions.map((option) => {
            const isActive = selectedFilter === option.key;
            const count = filterCounts[option.key] || 0;

            return (
              <button
                key={option.key}
                type="button"
                onClick={() => setSelectedFilter(option.key)}
                className={`project-texts-table-filter-button ${isActive ? 'is-active' : ''}`}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  position: 'relative',
                }}
              >
                <span
                  className="project-texts-table-filter-label"
                  style={{
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? '#000000' : '#6B7280',
                    transition: 'all 0.2s',
                  }}
                >
                  {option.label}
                </span>
                <span
                  className="project-texts-table-filter-count"
                  style={{
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#6B7280',
                    backgroundColor: '#F3F4F6',
                    borderRadius: '999px',
                    padding: '2px 8px',
                    minWidth: '20px',
                    textAlign: 'center',
                  }}
                >
                  {count}
                </span>
                {isActive && (
                  <div
                    className="project-texts-table-filter-underline"
                    style={{
                      position: 'absolute',
                      bottom: '-12px',
                      left: 0,
                      right: 0,
                      height: '3px',
                      backgroundColor: '#000000',
                    }}
                  />
                )}
              </button>
            );
          })}
          {!isSearchOpen ? (
            <>
              <img
                src="/Images/search-icon.png"
                alt="search"
                className="project-texts-table-search-toggle-icon"
                onClick={() => {
                  setIsSearchOpen(true);
                  setIsSortOpen(false);
                  setIsColumnsOpen(false);
                }}
                style={{
                  width: '32px',
                  height: '32px',
                  cursor: 'pointer',
                  marginLeft: 'auto',
                }}
              />

              <div
                className="project-texts-table-columns-new-content-wrapper"
                style={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: '-18px' }}
              >
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => setIsColumnsOpen((prev) => !prev)}
                    className="project-texts-table-columns-filter-button"
                    style={{
                      background: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      padding: '4px 10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <img
                      src="/Images/col-icon.png"
                      alt="columns"
                      style={{ width: '16px', height: '14px' }}
                    />
                    <span
                      className="project-texts-table-columns-filter-label"
                      style={{ fontSize: '14px', color: '#374151', fontWeight: 400 }}
                    >
                      {t('Colonnes')}
                    </span>
                  </button>

                  {isColumnsOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        right: 0,
                        marginTop: 8,
                        backgroundColor: '#FFFFFF',
                        borderRadius: 12,
                        boxShadow: '0 12px 30px rgba(15,23,42,0.25)',
                        padding: '10px 0',
                        minWidth: 260,
                        zIndex: 20,
                      }}
                    >
                      <div
                        style={{
                          padding: '4px 16px 10px',
                          fontSize: 15,
                          fontWeight: 600,
                          color: '#111827',
                        }}
                      >
                        {t('Colonnes visibles')}
                      </div>
                      <div
                        style={{
                          height: 1,
                          backgroundColor: '#F3F4F6',
                          marginBottom: 4,
                        }}
                      />
                      {[
                        { key: 'keywords', label: t('Mots clés') },
                        { key: 'uxScore', label: t('Score UX') },
                        { key: 'seoScore', label: t('Score SEO') },
                        { key: 'score', label: t('Score') },
                        { key: 'added', label: t('Ajout') },
                        { key: 'updated', label: t('Mise à jour') },
                        { key: 'status', label: t('Statut') },
                      ].map((opt) => {
                        const checked = !!visibleColumns[opt.key];
                        return (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() =>
                              setVisibleColumns((prev) => ({
                                ...prev,
                                [opt.key]: !prev[opt.key],
                              }))
                            }
                            style={{
                              width: '100%',
                              padding: '6px 18px',
                              background: 'transparent',
                              border: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              cursor: 'pointer',
                              fontSize: 14,
                              color: '#111827',
                            }}
                          >
                            <span style={{ width: 16, textAlign: 'center' }}>
                              {checked ? '✓' : ''}
                            </span>
                            <span>{opt.label}</span>
                          </button>
                        );
                      })}
                      <div
                        style={{
                          height: 1,
                          backgroundColor: '#F3F4F6',
                          marginTop: 6,
                        }}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setVisibleColumns({
                            keywords: true,
                            score: true,
                            added: true,
                            updated: true,
                            status: true,
                            uxScore: false,
                            seoScore: false,
                          })
                        }
                        style={{
                          width: '100%',
                          padding: '10px 18px',
                          background: 'transparent',
                          border: 'none',
                          textAlign: 'left',
                          fontSize: 14,
                          fontWeight: 500,
                          color: '#111827',
                          cursor: 'pointer',
                        }}
                      >
                        {t('Réinitialisé par défaut')}
                      </button>
                    </div>
                  )}
                </div>

                {onNewContentClick && (
                  <button
                    type="button"
                    onClick={onNewContentClick}
                    className="project-texts-table-new-content-button"
                    style={{
                      padding: '8px 14px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: '#5E45E6',
                      fontSize: 14,
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#F9FAFB';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <span style={{ fontSize: 20, lineHeight: 1 }}>+</span>
                    <span>{t('Nouveau contenu')}</span>
                  </button>
                )}
              </div>
            </>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginLeft: 'auto',
                flex: 1,
                maxWidth: '400px',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <img
                  src="/Images/search-icon2.png"
                  alt="search"
                  style={{
                    position: 'absolute',
                    left: '12px',
                    width: '16px',
                    height: '16px',
                    pointerEvents: 'none',
                  }}
                />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={t('Rechercher par mot-clé, rédacteur...')}
                  style={{
                    width: '100%',
                    padding: '8px 40px 8px 40px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                  autoFocus
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsSearchOpen(false);
                  setSearchValue('');
                }}
                style={{
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  padding: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                }}
              >
                <span style={{ fontSize: '16px', color: '#6B7280', lineHeight: 1 }}>×</span>
              </button>
            </div>
          )}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
            marginLeft: '16px',
            marginRight: '16px',
          }}
        >
          {generationMessage && (
            <div
              style={{
                padding: '12px 16px',
                borderRadius: '8px',
                border: 'none',
                display: 'flex',
                alignItems: isGenerationError ? 'flex-start' : 'center',
                gap: 12,
                fontSize: 14,
                color: isGenerationError ? '#B42318' : 'black',
                fontWeight: 500,
              }}
            >
              {!isGenerationError && (
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: 'black',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: '#FFFFFF',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    }}
                  />
                </div>
              )}
              <span style={{ whiteSpace: 'pre-line', lineHeight: 1.45 }}>{generationMessage}</span>
            </div>
          )}
        </div>

        <div
          className="project-texts-table-main-block"
          style={{
            borderRadius: 12,
            border: '1px solid #E5E7EB',
            overflow: 'visible',
            backgroundColor: '#FAFAFA',
          }}
        >
          <div
            className="project-texts-table-header-row"
            style={{
              display: 'grid',
              gridTemplateColumns: readOnly
                ? activeColumns.map((c) => c.width).join(' ')
                : `40px ${activeColumns.map((c) => c.width).join(' ')} 90px`,
              padding: '8px 16px',
              fontSize: 13,
              color: '#818181',
              backgroundColor: '#E9E9E9',
              alignItems: 'center',
            }}
          >
            {!readOnly && (
            <div>
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={(input) => {
                  if (input) input.indeterminate = isIndeterminate;
                }}
                onChange={handleSelectAll}
                style={{ accentColor: '#000000' }}
              />
            </div>
            )}
            {activeColumns.map((col) => (
              <div
                key={col.key}
                style={{
                  textAlign: col.align || 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  justifyContent: col.align === 'center' ? 'center' : 'flex-start',
                  cursor: col.sortable ? 'pointer' : 'default',
                }}
                onClick={() => col.sortable && handleColumnHeaderClick(col.key)}
              >
                <span>{col.header}</span>
                {col.sortable && (
                  <img
                    src="/Images/table-arrow.png"
                    alt="sort"
                    style={{
                      width: '12px',
                      height: '12px',
                      opacity: sortColumn === col.key ? 1 : 0.3,
                      transform: sortColumn === col.key && sortDirection === 'desc' ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'all 0.2s ease',
                    }}
                  />
                )}
              </div>
            ))}
            {!readOnly && (
            <div
              style={{
                textAlign: 'center',
                position: 'relative',
                paddingLeft: '8px',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: '-8px',
                  bottom: '-8px',
                  width: '1px',
                  backgroundColor: '#E5E7EB',
                }}
              />
              {t('Actions')}
            </div>
            )}
          </div>

          {hasSelection && selectedOnPageCount > 0 && (
            <div
              style={{
                padding: '8px 16px',
                fontSize: 13,
                color: 'black',
                backgroundColor: '#F5F3FF',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                fontWeight: 500
              }}
            >
              <span>
                {selectedOnPageCount === 1
                  ? t('Le contenu de cette page est sélectionné.')
                  : t('Les {{count}} contenus de cette page sont sélectionnés.', {
                    count: selectedOnPageCount,
                  })}
              </span>
              <button
                type="button"
                onClick={handleClearSelection}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 13,
                  color: '#6366F1',
                  textDecoration: 'none',
                  padding: 0,
                }}
              >
                {t('Effacer la selection')}
              </button>
            </div>
          )}

          {sortedRows.length > 0 ? (
            paginatedRows.map((text) => {
              const key = text.id ?? text.index;
              const createdAt = text.createdAt || text.dateCreation;
              const updatedAt =
                text.updatedAt || text.lastUpdate || text.modificationDate || null;

              return (
                <div
                  key={key}
                  className="project-texts-table-data-row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: readOnly
                      ? activeColumns.map((c) => c.width).join(' ')
                      : `40px ${activeColumns.map((c) => c.width).join(' ')} 90px`,
                    padding: '10px 16px',
                    fontSize: 12,

                    alignItems: 'center',
                    backgroundColor:
                      hoveredRowKey === key ? '#F9FAFB' : '#FFFFFF',
                    transition: 'background-color 0.15s ease-out',
                  }}
                  onMouseEnter={() => setHoveredRowKey(key)}
                  onMouseLeave={() => setHoveredRowKey(null)}
                >
                  {!readOnly && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTexts.has(key)}
                      onChange={(e) => handleSelectText(key, e.target.checked)}
                      style={{ accentColor: '#000000' }}
                    />
                  </div>
                  )}
                  {activeColumns.map((col) => {
                    if (col.key === 'keywords') {
                      return (
                        <div
                          key={col.key}
                          onClick={
                            readOnly && !textDetailsPath
                              ? undefined
                              : (e) => handleEditClick(text, e)
                          }
                          style={{
                            cursor:
                              readOnly && !textDetailsPath ? 'default' : 'pointer',
                          }}
                        >
                          <div
                            className="project-texts-table-keyword-title"
                            style={{
                              fontWeight: 500,
                              color: '#111827',
                              marginBottom: 2,
                              fontSize: 14,
                            }}
                          >
                            {text.keyword || '-'}
                          </div>
                          <div
                            className="project-texts-table-keyword-url"
                            style={{
                              fontSize: 11,
                              color: '#9CA3AF',
                              wordBreak: 'break-all',
                            }}
                          >
                            {getWebsiteUrl(text)}
                          </div>
                        </div>
                      );
                    }
                    if (col.key === 'score') {
                      return (
                        <div
                          key={col.key}
                          style={{ textAlign: 'center', fontWeight: 700 }}
                        >
                          {getTextScore(text)}
                        </div>
                      );
                    }
                    if (col.key === 'added') {
                      return (
                        <div key={col.key} style={{ textAlign: 'center' }}>
                          {formatDateSafe(createdAt)}
                        </div>
                      );
                    }
                    if (col.key === 'updated') {
                      return (
                        <div key={col.key} style={{ textAlign: 'center' }}>
                          {formatDateSafe(updatedAt)}
                        </div>
                      );
                    }
                    if (col.key === 'status') {
                      return (
                        <div
                          key={col.key}
                          style={{
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            position: 'relative',
                          }}
                        >
                          <img
                            src={getTextStatusIcon(text)}
                            alt={getTextStatusLabel(text)}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: readOnly ? 'default' : 'pointer',
                            }}
                            onClick={readOnly ? undefined : (e) => handleStatusIconClick(e, key)}
                          />
                          <span>{getTextStatusLabel(text)}</span>
                          {!readOnly && (
                          <img
                            src="/Images/status-icon.png"
                            alt="status icon"
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer',
                            }}
                            onClick={(e) => handleStatusIconClick(e, key)}
                          />
                          )}
                          {!readOnly && openStatusFor === key && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '120%',
                                right: 0,
                                backgroundColor: '#FFFFFF',
                                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)',
                                borderRadius: 8,
                                padding: '6px 0',
                                minWidth: 140,
                                zIndex: 10,
                              }}
                            >
                              {(isWriterClientView ? ['Brouillon', 'Livrés'] : ['Brouillon', 'Livrés', 'En révision', 'Validés', 'En lignes']).map(
                                (statusKey) => {
                                  const isActiveStatus =
                                    getStatusKey(text) === statusKey;
                                  return (
                                    <div
                                      key={statusKey}
                                      style={{
                                        padding: '6px 8px',
                                        fontSize: 12,
                                        color: isActiveStatus
                                          ? '#111827'
                                          : '#4B5563',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                        backgroundColor: isActiveStatus
                                          ? '#F1F1F1'
                                          : 'transparent',
                                        borderRadius: isActiveStatus ? 6 : 0,
                                        fontWeight: isActiveStatus ? 600 : 400,
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenStatusFor(null);
                                        if (!isActiveStatus && text?.id) {
                                          const previousItem = { ...text };
                                          const normalizedStatus =
                                            normalizeStatusKeyForHook(statusKey);
                                          handleTextStatusChange(
                                            text.id,
                                            normalizedStatus,
                                            previousItem
                                          );
                                        }
                                      }}
                                    >
                                      <img
                                        src={getStatusIconByKey(statusKey)}
                                        alt={statusKey}
                                        style={{ width: 14, height: 14 }}
                                      />
                                      <span>{t(statusKey)}</span>
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                    if (col.key === 'uxScore') {
                      const uxScore = text?.contentScore?.uxScore ?? null;
                      const displayScore = typeof uxScore === 'number' ? `${Math.round(uxScore)}%` : '-';
                      return (
                        <div
                          key={col.key}
                          style={{ textAlign: 'center', fontWeight: 700 }}
                        >
                          {displayScore}
                        </div>
                      );
                    }
                    if (col.key === 'seoScore') {
                      const seoScore = text?.contentScore?.seoScore ?? null;
                      const displayScore = typeof seoScore === 'number' ? `${Math.round(seoScore)}%` : '-';
                      return (
                        <div
                          key={col.key}
                          style={{ textAlign: 'center', fontWeight: 700 }}
                        >
                          {displayScore}
                        </div>
                      );
                    }
                    return <div key={col.key} />;
                  })}
                  {!readOnly && (
                  <div
                    style={{
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      borderLeft: '1px solid #E5E7EB',
                      paddingLeft: '8px',
                      position: 'relative',
                    }}
                  >
                    <img
                      src="/Images/edit-icon.png"
                      alt="edit icon"
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      onClick={(e) => handleEditClick(text, e)}
                    />
                    <img
                      src="/Images/plus-icon.png"
                      alt="plus icon"
                      style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      onClick={(e) => handleActionsClick(e, key)}
                    />
                    {openActionsFor === key && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '120%',
                          right: 0,
                          backgroundColor: '#FFFFFF',
                          boxShadow: '0 8px 24px rgba(15,23,42,0.18)',
                          borderRadius: 10,
                          padding: '8px 0',
                          minWidth: 170,
                          zIndex: 15,
                        }}
                      >
                        <button
                          type="button"
                          style={{
                            width: '100%',
                            padding: '6px 14px',
                            textAlign: 'left',
                            background: 'transparent',
                            border: 'none',
                            fontSize: 13,
                            color: '#111827',
                            cursor: 'pointer',
                            display: 'block',
                          }}
                          onClick={async () => {
                            try {
                              await DownloadTextWordType(text.id);
                            } catch (e) {

                            } finally {
                              setOpenActionsFor(null);
                            }
                          }}
                        >
                          {t('Telecharger le texte')}
                        </button>

                        <div
                          style={{
                            borderTop: '1px solid #F3F4F6',
                            margin: '4px 0',
                          }}
                        />
                        <button
                          type="button"
                          style={{
                            width: '90%',
                            padding: '6px 14px',
                            textAlign: 'left',
                            background: '#F1F1F1',
                            border: 'none',
                            fontSize: 13,
                            color: '#B91C1C',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            borderRadius: '6px',
                            marginLeft: '5%',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteConfirmation([text]);
                            setOpenActionsFor(null);
                          }}
                        >
                          <img
                            src="/Images/icon-trash.png"
                            alt="delete"
                            style={{ width: 25, height: 25 }}
                          />
                          <span>{t('Supprimer')}</span>
                        </button>
                      </div>
                    )}
                  </div>
                  )}
                </div>
              );
            })
          ) : (
            <div
              style={{
                padding: '24px 16px',
                textAlign: 'center',
                color: '#9CA3AF',
                fontSize: 13,
                backgroundColor: '#FFFFFF',
              }}
            >
              {t('Aucun texte disponible')}
            </div>
          )}
        </div>

        {sortedRows.length > 0 && (
          <TablePagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disablePrev={safeCurrentPage === 1}
            disableNext={safeCurrentPage === totalPages || sortedRows.length === 0}
          />
        )}

        {!readOnly && hasSelection && (
          <div
            style={{
              position: 'fixed',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 16,
              backgroundColor: 'black',
              borderRadius: '6px',
              boxShadow: '0 6px 18px rgba(15,23,42,0.18)',
              display: 'flex',
              alignItems: 'center',
              padding: '10px 18px',
              gap: 24,
              fontSize: 13,
              zIndex: 30,
              color: 'white',

            }}
          >
            <button
              type="button"
              onClick={handleClearSelection}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                color: 'white',
                justifyContent: 'center',
              }}
            >
              <img
                src="/Images/close-icon1.png"
                alt="close selection"
                style={{ width: 14, height: 14 }}
              />
            </button>

            <div
              style={{
                width: 1,
                height: 24,
                backgroundColor: '#E5E7EB',
                margin: '0 -10px',
              }}
            />

            <span style={{ color: 'white', fontWeight: 500 }}>
              {selectedTexts.size}{' '}
              {selectedTexts.size === 1
                ? t('contenu sélectionné')
                : t('contenus sélectionnés')}
            </span>

            <div
              style={{
                width: 1,
                height: 24,
                backgroundColor: '#E5E7EB',
              }}
            />

            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <button
                type="button"
                onClick={() => setIsDownloadMenuOpen((prev) => !prev)}
                style={{
                  background: 'transparent',
                  border: '1px solid rgba(249,250,251,0.4)',
                  borderRadius: '6px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  color: 'white',
                  whiteSpace: 'nowrap',
                  fontWeight: 500,
                }}
              >
                <img
                  src="/Images/upload-icon.png"
                  alt="download texts"
                  style={{ width: 14, height: 14 }}
                />
                <span>{t('Telecharger les textes')}</span>
                <img
                  src="/Images/white-arrow.png"
                  alt="open download menu"
                  style={{
                    width: 14,
                    height: 14,
                    transform: isDownloadMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s ease-out',
                  }}
                />
              </button>

              {isDownloadMenuOpen && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '110%',
                    right: 0,
                    backgroundColor: '#111827',
                    borderRadius: 6,
                    boxShadow: '0 6px 18px rgba(15,23,42,0.18)',
                    padding: '6px 0',
                    minWidth: 260,
                    zIndex: 40,
                  }}
                >
                  <button
                    type="button"
                    onClick={handleDownloadAllExcel}
                    style={{
                      width: '100%',
                      padding: '6px 14px',
                      textAlign: 'left',
                      background: 'transparent',
                      border: 'none',
                      fontSize: 13,
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>
                      {t('Télécharger tous les textes')} ({rows.length})
                    </span>
                  </button>

                  <div
                    style={{
                      height: 1,
                      margin: '4px 0',
                      backgroundColor: '#374151',
                      opacity: 0.7,
                    }}
                  />

                  <button
                    type="button"
                    onClick={handleDownloadSelectedExcel}
                    style={{
                      width: '100%',
                      padding: '6px 14px',
                      textAlign: 'left',
                      background: 'transparent',
                      border: 'none',
                      fontSize: 13,
                      color: 'white',
                      cursor: selectedTexts.size > 0 ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      opacity: selectedTexts.size > 0 ? 1 : 0.5,
                    }}
                    disabled={selectedTexts.size === 0}
                  >
                    <span>
                      {selectedTexts.size === 1
                        ? t('Télécharger le texte')
                        : t('Télécharger les {{count}} textes', {
                          count: selectedTexts.size,
                        })}
                    </span>
                  </button>
                </div>
              )}
            </div>

            <div
              style={{
                width: 1,
                height: 24,
                backgroundColor: '#E5E7EB',
              }}
            />

            <button
              type="button"
              onClick={() => openDeleteConfirmation(selectedRows)}
              style={{
                background: 'transparent',
                border: '1px solid rgba(249,250,251,0.4)',
                borderRadius: '6px',
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: 13,
                color: 'white',
                whiteSpace: 'nowrap',
                fontWeight: 500,
              }}
            >
              {t('Supprimer')}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

export default ProjectTextsTable;